import { GeneralAPI } from '../apis/GeneralAPI'
import Header from '../components/Header'
import LandingPageWrapper from '../components/HomePage/LandingPageWrapper'
import BookingSection from '../components/PestLanding/BookingSection'
import CampaignStrategy from '../components/PestLanding/CampaignStrategy'
import FAQ from '../components/PestLanding/FAQ'
import HowItWorks from '../components/PestLanding/HowItWorks'
import PestHero from '../components/PestLanding/PestHero'
import SocialProof from '../components/PestLanding/SocialProof'
import WhyOLM from '../components/PestLanding/WhyOLM'

const ECOMMERCE_FALLBACK_META = `
<meta name="description" content="Connect Shopify to direct mail campaigns that reach shoppers at exactly the right moment, from abandoned carts and first purchase to reviews, replenishment, upcoming sales, and winbacks." />
<meta property="og:title" content="Ecommerce Direct Mail That Converts" />
<meta property="og:description" content="Connect Shopify to direct mail campaigns that reach shoppers at exactly the right moment, from abandoned carts and first purchase to reviews, replenishment, upcoming sales, and winbacks." />
`

const HERO_BULLETS = [
    'Trigger personalized mail from Shopify customer, order, and cart activity',
    'Support acquisition, retention, review generation, replenishment, and winback goals',
    'Fast in-house production: campaigns move from trigger to mailbox without weeks of delay',
    'Track response with offer codes, QR codes, and campaign-level reporting',
]

const CAMPAIGNS = [
    {
        title: 'Abandoned carts',
        description:
            'Follow up on high-intent carts with a personalized postcard, offer, or reminder that stands out offline.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'refresh',
        badge: 'Shopify trigger',
    },
    {
        title: 'Customer acquisition',
        description:
            'Reach qualified prospects with offers, catalogs, samples, and creative built to drive a first purchase.',
        iconBg: 'bg-[#FAB331]/20',
        iconColor: 'text-secondary',
        icon: 'target',
    },
    {
        title: 'Reviews + referrals',
        description:
            'Automatically thank recent buyers and ask for a review, referral, or user-generated content.',
        iconBg: 'bg-[#DBEAFE]',
        iconColor: 'text-[#1E40AF]',
        icon: 'star',
        badge: 'Shopify trigger',
    },
    {
        title: 'Win back customers',
        description:
            'Re-engage shoppers who have not purchased in 60, 90, or 180 days with a reason to return.',
        iconBg: 'bg-[#DCFCE7]',
        iconColor: 'text-[#166534]',
        icon: 'refresh',
    },
    {
        title: 'Upcoming sales',
        description:
            'Build anticipation for launches, seasonal promotions, VIP events, and limited-time offers.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'bolt',
    },
    {
        title: 'Loyalty programs',
        description:
            'Prompt timely reorders, reward VIP customers, and keep your brand present between purchases.',
        iconBg: 'bg-gray-200',
        iconColor: 'text-gray-600',
        icon: 'gift',
    },
]

const COMPARISONS = [
    {
        bad: {
            title: 'Uploaded lists and one-off sends',
        },
        good: {
            title: 'Shopify-connected customer triggers',
        },
    },
    {
        bad: {
            title: 'Slow campaign launches',
        },
        good: {
            title: 'Fast in-house production',
        },
    },
    {
        bad: {
            title: 'Generic postcard creative',
        },
        good: {
            title: 'Lifecycle-specific offers and creative',
        },
    },
    {
        bad: {
            title: 'No response visibility',
        },
        good: {
            title: 'Trackable codes, QR scans, and campaigns',
        },
    },
    {
        bad: {
            title: 'Disconnected direct mail orders',
        },
        good: {
            title: 'Repeatable acquisition and retention program',
        },
    },
]

const STEPS = [
    {
        num: 1,
        color: '#ED5C2F',
        title: 'Lifecycle strategy',
        desc: 'We review your store, customer journey, purchase cycle, current channels, and the biggest opportunities for acquisition, retention, and winback.',
    },
    {
        num: 2,
        color: '#F28230',
        title: 'Triggers + offers',
        desc: 'Choose the Shopify events, audiences, timing, formats, and incentives that should trigger each mail experience.',
    },
    {
        num: 3,
        color: '#FAB331',
        title: 'Create + automate',
        desc: 'We develop the creative, prepare the automation, and produce each piece through our in-house print and mail operation.',
    },
    {
        num: 4,
        color: '#3F454F',
        title: 'Measure + expand',
        desc: 'Track redemptions and response, compare audiences and offers, then add new lifecycle campaigns as the program proves out.',
    },
]

const FAQS = [
    {
        question: 'How do Shopify direct mail automations work?',
        answer: 'Customer and order events in Shopify can place shoppers into specific mail campaigns. Common triggers include an abandoned cart, first purchase, delivery, time since last purchase, customer segment, VIP status, or an upcoming promotional window.',
    },
    {
        question: 'What campaigns should an ecommerce brand start with?',
        answer: 'The best starting point depends on your economics and customer journey. High-intent abandoned carts, review requests after delivery, second-purchase campaigns, lapsed-customer winbacks, and advance notice for major sales are strong places to begin.',
    },
    {
        question: 'Can direct mail work alongside email and SMS?',
        answer: 'Yes. Direct mail works best as part of the lifecycle mix. It can reinforce an email or SMS sequence, reach shoppers who have tuned out digital channels, and give high-value customer moments more weight.',
    },
    {
        question: 'Is this only for large ecommerce companies?',
        answer: 'No fixed company size is required, but direct mail works best when you have enough customer or prospect volume, healthy order economics, and a repeatable use case. We help prioritize campaigns where the expected value supports the investment.',
    },
]

export default function Ecommerce({ data }) {
    return (
        <LandingPageWrapper sectionSpacing={false} paddingFromTop={false}>
            <Header
                meta={data?.yoast_head || ECOMMERCE_FALLBACK_META}
                metaTitle="Ecommerce Direct Mail That Converts"
            />
            <PestHero
                eyebrow="For Ecommerce Brands"
                heading={['Turn customer moments', 'into mail that converts.']}
                description="Connect Shopify to direct mail campaigns that reach shoppers at exactly the right moment, from first purchase and abandoned cart to reviews, replenishment, upcoming sales, and winbacks."
                cta="Plan your ecommerce mail strategy"
                fitText="Built for ecommerce teams that want direct mail to work like a lifecycle channel, not a disconnected one-off campaign."
                bullets={HERO_BULLETS}
                image={{
                    src: '/assets/homepage/ecommerce-hero.png',
                    alt: 'Direct mail formats for automated ecommerce lifecycle campaigns',
                    width: 1124,
                    height: 992,
                }}
            />
            <SocialProof eyebrow="Trusted by growing businesses across the U.S." />
            <CampaignStrategy
                eyebrow="Shopify direct mail automations"
                title="Direct mail for every high-value moment in the customer journey."
                description="Use the customer signals already inside Shopify to send timely, personalized mail that earns the first order, drives the next one, and brings lapsed shoppers back."
                campaigns={CAMPAIGNS}
            />
            <WhyOLM
                title="Built for ecommerce teams that expect automation—not manual mail merges."
                paragraphs={[
                    'Email and SMS are crowded. Direct mail gives your brand a physical moment of attention while still using the same lifecycle signals that power your digital marketing.',
                    'Open Letter Marketing connects strategy, creative, data, printing, and mailing so Shopify events can become repeatable campaigns across acquisition, retention, and winback.',
                ]}
                comparisons={COMPARISONS}
            />
            <HowItWorks
                title="Launch Shopify-powered direct mail"
                gradientTitle="without adding another manual workflow."
                description="We map the customer moments that matter, connect the right Shopify data, and build campaigns your team can run automatically or schedule around key promotions."
                steps={STEPS}
            />
            <FAQ title="Questions ecommerce teams ask first." faqs={FAQS} />
            <BookingSection
                titleHighlight="ecommerce direct mail strategy call."
                description="Tell us about your store, customer journey, and growth goals. We'll identify the best opportunities for acquisition, retention, and winbacks."
                containerId="ecommerce-landing-page"
                classPrefix="ecommerce"
            />

            <style
                dangerouslySetInnerHTML={{
                    __html: `
                @media (max-width: 640px) {
                    .ecommerce-form-grid {
                        grid-template-columns: 1fr !important;
                        padding: 20px !important;
                        gap: 16px !important;
                    }
                    .ecommerce-form-footer {
                        justify-content: center !important;
                        text-align: center;
                    }
                    .ecommerce-form-footer button {
                        width: 100%;
                    }
                }
            `,
                }}
            />
        </LandingPageWrapper>
    )
}

export async function getStaticProps() {
    let data = {}

    try {
        const page = await GeneralAPI.getPageBySlug('/ecommerce')
        data = page ? page[0] || {} : {}
    } catch (e) {
        console.error(e)
    }

    try {
        const { banners } = await GeneralAPI.getACFBanners()
        data.banners = banners
    } catch (e) {
        console.error(e)
    }

    return {
        props: {
            data,
            hideCartButton: true,
            hideLoginButton: true,
            phoneNumber: '978-893-5890',
        },
        revalidate: 10,
    }
}
